import logger from '../../../lib/logger';
import { VideoStreamMerger } from '../../../lib/streamMerger';
import AudioStreamMerger from './AudioStreamMerger';

const DEFAULT_USER_VIDEO_CONSTRAINTS = {
  width: { max: 320 },
  height: { max: 320 },
  frameRate: { max: 30 },
};

const DEFAULT_DISPLAY_VIDEO_CONSTRAINTS = {
  cursor: 'always',
  logicalSurface: true,
  width: window.screen.width,
  height: window.screen.height,
  frameRate: { max: 30 },
};

const DEFAULT_DISPLAY_AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  sampleRate: 48000,
};

export const streams = {
  userMediaStream: new MediaStream(),
  displayMediaStream: new MediaStream(),
  mergedStream: new MediaStream(),
};

export const deviceAudioStream = new MediaStream();

let videoStreamMerger: VideoStreamMerger;
let audioStreamMerger: AudioStreamMerger;

type TrackInfo = Pick<MediaStreamTrack, 'id' | 'kind' | 'label'>;

export const replaceTracks = ({
  newStream,
  streamType,
}: {
  newStream: MediaStream;
  streamType: keyof typeof streams;
}): {
  streamId: string;
  trackInfo: TrackInfo[];
} => {
  const stream = streams[streamType];
  if (!newStream) throw new Error(`New ${streamType} is required`);
  const newTracks = newStream.getTracks();
  const existingTracks = stream?.getTracks() ?? [];
  existingTracks
    .filter((existingTrack) =>
      newTracks.find((newTrack) => newTrack.kind === existingTrack.kind),
    )
    .forEach((track) => {
      track.stop();
      stream?.removeTrack(track);
    });
  newTracks.forEach((track) => {
    stream?.addTrack(track);
  });
  return {
    streamId: stream.id,
    trackInfo: stream.getTracks().map(({ id, kind, label }) => ({
      id,
      kind,
      label,
    })),
  };
};

export const getUserMediaStream = async (
  userMediaConstraints?: Parameters<MediaDevices['getUserMedia']>[0],
): Promise<ReturnType<typeof replaceTracks>> => {
  if (!userMediaConstraints) {
    throw new Error('User media constraints are required');
  }
  let { video } = userMediaConstraints;
  const { audio } = userMediaConstraints;
  // If user sets a value to video or audio (even if it's false), we will stop the existing track
  const trackTypesToBeUpdated = Object.keys(userMediaConstraints).filter(
    (key) => userMediaConstraints[key as 'audio' | 'video'] !== undefined,
  ) as ('video' | 'audio')[];
  const { userMediaStream } = streams;
  if (trackTypesToBeUpdated.length > 0) {
    trackTypesToBeUpdated.forEach((trackType) => {
      const track = userMediaStream
        .getTracks()
        .find((t) => t.kind === trackType);
      if (track) {
        logger.log(`Stopping track ${track.kind} ${track.id}`);
        track.stop();
        userMediaStream.removeTrack(track);
      }
    });
  }
  video = video
    ? {
        ...DEFAULT_USER_VIDEO_CONSTRAINTS,
        ...(typeof video === 'object' ? video : {}),
      }
    : false;
  const constraints = {
    video,
    audio,
  };
  if (audio || video) {
    const newUserMediaStream =
      await navigator.mediaDevices.getUserMedia(constraints);
    newUserMediaStream.getTracks().forEach((track) => {
      userMediaStream.addTrack(track);
    });
  }

  const trackInfo = userMediaStream.getTracks().map(({ id, kind, label }) => ({
    id,
    kind,
    label,
  }));
  return { streamId: userMediaStream.id, trackInfo };
};

export const getDisplayMediaStream = async (
  displayMediaConstraints: Parameters<MediaDevices['getDisplayMedia']>[0],
): Promise<ReturnType<typeof replaceTracks>> => {
  if (!displayMediaConstraints) {
    throw new Error('Display media constraints are required');
  }
  let { video, audio } = displayMediaConstraints;
  video = video
    ? {
        ...DEFAULT_DISPLAY_VIDEO_CONSTRAINTS,
        ...(typeof video === 'object' ? video : {}),
      }
    : false;
  audio = audio
    ? {
        ...DEFAULT_DISPLAY_AUDIO_CONSTRAINTS,
        ...(typeof audio === 'object' ? audio : {}),
      }
    : false;
  const newStream = await navigator.mediaDevices.getDisplayMedia({
    video,
    audio,
  });
  return replaceTracks({
    newStream,
    streamType: 'displayMediaStream',
  });
};

export const getAudioMediaStream = async (
  audioConstraints?: Parameters<MediaDevices['getUserMedia']>[0],
): Promise<ReturnType<typeof replaceTracks>> => {
  if (!audioConstraints) {
    throw new Error('Audio constraints are required');
  }

  // Stop and remove existing audio tracks
  const existingAudioTrack = deviceAudioStream.getAudioTracks()[0];
  if (existingAudioTrack) {
    logger.log(`Stopping audio track ${existingAudioTrack.id}`);
    existingAudioTrack.stop();
    deviceAudioStream.removeTrack(existingAudioTrack);
  }

  // Apply default audio constraints if not provided
  const constraints = {
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      sampleRate: 48000,
      ...audioConstraints,
    },
    video: false,
  };

  try {
    // First check if the device exists
    const devices = await navigator.mediaDevices.enumerateDevices();
    const deviceExists = devices.some(
      (device) =>
        device.kind === 'audioinput' &&
        typeof audioConstraints.audio === 'object' &&
        device.deviceId === audioConstraints.audio.deviceId,
    );

    if (!deviceExists) {
      throw new Error('Audio device not found');
    }

    const newAudioStream =
      await navigator.mediaDevices.getUserMedia(constraints);

    // Add new audio track to the stream
    newAudioStream.getAudioTracks().forEach((track) => {
      deviceAudioStream.addTrack(track);
    });

    const trackInfo = deviceAudioStream
      .getTracks()
      .map(({ id, kind, label }) => ({
        id,
        kind,
        label,
      }));

    return { streamId: deviceAudioStream.id, trackInfo };
  } catch (error) {
    logger.error('Error getting audio stream:', error);
    throw error;
  }
};

export const refreshAudioStreamMerger = async () => {
  if (audioStreamMerger) {
    await audioStreamMerger.refreshStreams();
  }
};

export const mergeStreams = async () => {
  const { userMediaStream, displayMediaStream, mergedStream } = streams;
  videoStreamMerger = new VideoStreamMerger();
  audioStreamMerger = new AudioStreamMerger();
  let audioTrack;
  let videoTrack;

  // If there is at least one audio track, initiate audio mixer
  if (
    displayMediaStream.getAudioTracks().length > 0 ||
    userMediaStream.getAudioTracks().length > 0
  ) {
    audioStreamMerger.addStream(displayMediaStream);
    audioStreamMerger.addStream(userMediaStream);
    [audioTrack] = audioStreamMerger.getOutputStream().getAudioTracks();
  }

  // If both user and display video tracks are available,
  // Use stream videoStreamMerger to merge them into a sigle video track
  if (
    displayMediaStream.getVideoTracks().length > 0 &&
    userMediaStream.getVideoTracks().length > 0
  ) {
    // Capture initial dimensions at recording start
    const {
      width: initialDisplayWidth = window.screen.width,
      height: initialDisplayHeight = window.screen.height,
    } = displayMediaStream.getVideoTracks()[0].getSettings() || {};

    // Set initial output size (will be dynamically updated if window resizes)
    videoStreamMerger.setOutputSize(initialDisplayWidth, initialDisplayHeight);

    // Calculate initial webcam diameter based on initial dimensions
    const initialCamDiameter =
      Math.min(initialDisplayWidth, initialDisplayHeight) / 4;

    videoStreamMerger.addStream(displayMediaStream, {
      draw: (ctx, frame, done) => {
        // Get current track dimensions (may have changed due to window resize)
        const displayTrack = displayMediaStream.getVideoTracks()[0];
        const currentSettings = displayTrack?.getSettings() || {};
        const currentWidth = currentSettings.width || initialDisplayWidth;
        const currentHeight = currentSettings.height || initialDisplayHeight;

        // Dynamically update canvas size if track dimensions changed
        // This ensures the video always fills the canvas without distortion
        if (
          currentWidth !== videoStreamMerger.width ||
          currentHeight !== videoStreamMerger.height
        ) {
          videoStreamMerger.setOutputSize(currentWidth, currentHeight);
        }

        // Clear canvas
        ctx?.save();
        ctx.fillStyle = '#000000';
        ctx?.fillRect(0, 0, currentWidth, currentHeight);

        // Draw the video frame at full size (no distortion since canvas matches source)
        ctx?.drawImage(frame, 0, 0, currentWidth, currentHeight);

        ctx?.restore();
        done();
      },
      // @ts-ignore
      audioEffect: null,
      height: videoStreamMerger.height,
      mute: !(displayMediaStream.getAudioTracks().length > 0),
      muted: !(displayMediaStream.getAudioTracks().length > 0),
      width: videoStreamMerger.width,
      x: 0,
      y: 0,
      index: 0,
    });

    videoStreamMerger.addStream(userMediaStream, {
      height: initialCamDiameter,
      width: initialCamDiameter,
      index: 1,
      draw: (ctx, frame, done) => {
        // Get current display track dimensions
        const displayTrack = displayMediaStream.getVideoTracks()[0];
        const currentSettings = displayTrack?.getSettings() || {};
        const currentDisplayWidth =
          currentSettings.width || initialDisplayWidth;
        const currentDisplayHeight =
          currentSettings.height || initialDisplayHeight;

        // Scale webcam diameter proportionally to current canvas size
        const camDiameter =
          Math.min(currentDisplayWidth, currentDisplayHeight) / 4;

        ctx?.save();
        // Position webcam in bottom-left corner
        const radius = camDiameter / 2;
        const x = 10;
        const y = currentDisplayHeight - camDiameter - 10;

        // circular shape camera preview
        videoStreamMerger.circularImage(ctx, x, y, radius);
        ctx?.clip();

        // Calculate the source dimensions to maintain aspect ratio
        const track = userMediaStream.getVideoTracks()[0];
        const settings = track.getSettings();
        const sourceWidth = settings.width || camDiameter;
        const sourceHeight = settings.height || camDiameter;
        const sourceAspectRatio = sourceWidth / sourceHeight;
        const targetAspectRatio = 1; // We want a square for the circular crop

        let drawWidth;
        let drawHeight;
        let offsetX;
        let offsetY;

        if (sourceAspectRatio > targetAspectRatio) {
          // Source is wider, crop the sides
          drawHeight = camDiameter;
          drawWidth = drawHeight * sourceAspectRatio;
          offsetX = (drawWidth - camDiameter) / 2;
          offsetY = 0;
        } else {
          // Source is taller, crop the top and bottom
          drawWidth = camDiameter;
          drawHeight = drawWidth / sourceAspectRatio;
          offsetX = 0;
          offsetY = (drawHeight - camDiameter) / 2;
        }

        ctx?.drawImage(frame, x - offsetX, y - offsetY, drawWidth, drawHeight);
        ctx?.restore();
        done();
      },
      // @ts-ignore
      audioEffect: null,
      mute: true,
      muted: true,
      x: 10,
      y: videoStreamMerger.height - initialCamDiameter - 10,
    });

    videoStreamMerger.start(); // merge screen stream and camera stream
    [videoTrack] = videoStreamMerger.result?.getVideoTracks() || [];
  } else if (
    displayMediaStream.getVideoTracks().length > 0 &&
    !userMediaStream.getVideoTracks().length
  ) {
    [videoTrack] = displayMediaStream.getVideoTracks();
  } else if (
    !displayMediaStream.getVideoTracks().length &&
    userMediaStream.getVideoTracks().length > 0
  ) {
    [videoTrack] = userMediaStream.getVideoTracks();
  }

  if (videoTrack) {
    mergedStream.addTrack(videoTrack);
  }
  if (audioTrack) {
    mergedStream.addTrack(audioTrack);
  }
};

// Debug helper to check active media tracks in the browser
export const debugActiveMediaTracks = async () => {
  const getTrackSummary = (t: MediaStreamTrack) => `${t.kind}:${t.readyState}`;

  const allStreams = {
    user: streams.userMediaStream.getTracks().map(getTrackSummary),
    display: streams.displayMediaStream.getTracks().map(getTrackSummary),
    merged: streams.mergedStream.getTracks().map(getTrackSummary),
    deviceAudio: deviceAudioStream.getTracks().map(getTrackSummary),
  };

  console.log('[MicDebug] Active tracks:', allStreams);
};

export const cleanup = () => {
  // Collect track info BEFORE cleanup
  const tracksBefore: Record<string, string[]> = {};
  Object.keys(streams).forEach((key) => {
    const stream = streams[key as keyof typeof streams];
    tracksBefore[key] = stream
      .getTracks()
      .map((t) => `${t.kind}:${t.label}:${t.readyState}`);
  });
  tracksBefore.deviceAudio = deviceAudioStream
    .getTracks()
    .map((t) => `${t.kind}:${t.label}:${t.readyState}`);

  console.log('[MicDebug] Cleanup started', {
    tracksBefore,
    videoMergerCtx: videoStreamMerger?.getAudioContext()?.state || 'null',
    audioMergerTracks:
      audioStreamMerger
        ?.getOutputStream()
        .getTracks()
        .map((t) => t.readyState) || [],
  });

  // Stop all tracks in main streams
  let stoppedCount = 0;
  const failedStops: string[] = [];

  Object.keys(streams).forEach((key) => {
    const stream = streams[key as keyof typeof streams];
    stream.getTracks().forEach((track) => {
      const before = track.readyState;
      track.stop();
      const after = track.readyState;
      if (after === 'ended') {
        stoppedCount += 1;
      } else {
        failedStops.push(`${key}:${track.kind}:${before}->${after}`);
      }
    });
    streams[key as keyof typeof streams] = new MediaStream();
  });

  // Stop deviceAudioStream tracks
  deviceAudioStream.getTracks().forEach((track) => {
    const before = track.readyState;
    track.stop();
    const after = track.readyState;
    if (after === 'ended') {
      stoppedCount += 1;
    } else {
      failedStops.push(`deviceAudio:${track.kind}:${before}->${after}`);
    }
    deviceAudioStream.removeTrack(track);
  });

  // Stop mergers
  if (videoStreamMerger) {
    videoStreamMerger.stop();
    // @ts-ignore
    videoStreamMerger = null;
  }
  if (audioStreamMerger) {
    audioStreamMerger.stop();
    // @ts-ignore
    audioStreamMerger = null;
  }

  console.log('[MicDebug] Cleanup complete', {
    stoppedTracks: stoppedCount,
    failedStops: failedStops.length > 0 ? failedStops : 'none',
  });

  // Single delayed verification
  setTimeout(() => {
    const remaining = [
      ...streams.userMediaStream.getTracks(),
      ...streams.displayMediaStream.getTracks(),
      ...streams.mergedStream.getTracks(),
      ...deviceAudioStream.getTracks(),
    ];
    const live = remaining.filter((t) => t.readyState === 'live');
    if (live.length > 0) {
      console.error(
        '[MicDebug] LEAK: tracks still live:',
        live.map((t) => `${t.kind}:${t.label}`),
      );
    } else {
      console.log('[MicDebug] All tracks ended, mic should be released');
    }
  }, 3000);
};
